import { TZDate } from '@date-fns/tz'
import { createSearchParams } from 'react-router'
import { format, addDays } from 'date-fns'
import 'jspdf-autotable'

import { Event } from '~/components/Events'

export type ArtistSearchBy = 'tags' | 'artist'

export type HostSearchBy = 'tags' | 'host'

export interface CreatableSelectOption {
  id: number
  label: string
  value: string
  __isNew__?: boolean
}

export interface ArtistOption {
  label: string
  value: number
  picture?: string
  __isNew__?: boolean
}

export const STRIPE_TEST_PUBLISHABLE_KEY =
  'pk_test_51RULczIZL7S0wUKXR3vPmzIZUSMAfWqIkNhPfCRQeTZJ5a71Qv2NzEU5urNR9waAxC4xWofylHjQTEV16VF1LM4Q00mEtyB85R'
export const STRIPE_LIVE_PUBLISHABLE_KEY =
  'pk_live_51RULctIzM0XZo0oCpZqp80yrj21GdNOdndhl68R22gtjpsd245KBQye7yQJ7zHNo6j3gXPnUoLowI7vhcAUCOcTh00AGnXvvKB'

export const STORAGE_DOMAIN = 'https://fly.storage.tigris.dev'
export const BUCKET_NAME = 'fannaan'
// VERY IMPORTANT!! These constants should be kept in line with the
// ones here app/utils/index.server.ts
// This is 2%
export const PERCENTAGE_OF_TOTAL_RATE = 0.02
// This is in cents
export const PER_TICKET_RATE = 50

export type City =
  | 'melbourne_australia'
  | 'sydney_australia'
  | 'austin_usa'
  | 'houston_usa'
  | 'san_antonio_usa'

export const CITY_OPTIONS = [
  { label: 'Naarm/Melbourne - Australia', value: 'melbourne_australia' },
  { label: 'Sydney - Australia', value: 'sydney_australia' },
  // { label: 'Austin - USA', value: 'austin_usa' },
  // { label: 'Houston - USA', value: 'houston_usa' },
  // { label: 'San Antonio - USA', value: 'san_antonio_usa' },
]

export const CITY_LABELS: Record<City, string> = {
  melbourne_australia: 'Naarm/Melbourne',
  sydney_australia: 'Sydney',
  austin_usa: 'Austin',
  houston_usa: 'Houston',
  san_antonio_usa: 'San Antonio',
}

export type Category =
  | 'Music'
  | 'Comedy'
  | 'Art'
  | 'Fundraiser'
  | 'Classes'
  | 'Poetry'

export const CATEGORY_OPTIONS: Array<{ label: Category; value: Category }> = [
  { label: 'Music', value: 'Music' },
  { label: 'Comedy', value: 'Comedy' },
  { label: 'Art', value: 'Art' },
  { label: 'Poetry', value: 'Poetry' },
  { label: 'Fundraiser', value: 'Fundraiser' },
  { label: 'Classes', value: 'Classes' },
]

export const dateTZ = (date?: string | Date) =>
  date
    ? typeof date === 'string'
      ? TZDate.tz('Australia/Melbourne', date)
      : date
    : TZDate.tz('Australia/Melbourne')

export const formatDate = (date: Date | string) => format(date, 'do MMM yyyy')

export const formatTime = (time: string) => {
  const [hours, mins, seconds] = time.split(':')

  return format(
    new Date().setHours(parseInt(hours), parseInt(mins), parseInt(seconds)),
    "K':'mm bbb"
  )
}

export const capitalize = (text: string) =>
  text
    .split(' ')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.substring(1)} `)
    .join(' ')

export const isArabic = (text: string) =>
  /[\u0600-\u06FF\u0750-\u077F]/.test(text)

export const formatUrl = (url: string) =>
  url.match(/https:\/\//) === null ? `https://${url}` : url

// Add helper function to calculate time remaining
export const getTimeRemaining = (reservedAt: string | Date) => {
  const reserved =
    typeof reservedAt === 'string'
      ? new Date(reservedAt).getTime()
      : reservedAt.getTime()
  const expires = reserved + 20 * 60 * 1000 // 20 minutes in milliseconds
  const now = new Date().getTime()
  const timeLeft = expires - now

  return {
    total: timeLeft,
    minutes: Math.floor((timeLeft / 1000 / 60) % 60),
    seconds: Math.floor((timeLeft / 1000) % 60),
  }
}

export const getGoogleCalendarUrl = (event: Event) => {
  const formattedStartTime = event.startTime.replaceAll(':', '')
  const formattedEndTime = event.endTime
    ? event.endTime.replaceAll(':', '')
    : formattedStartTime

  const hours = (event.endTime ? event.endTime : event.startTime).split(':')[0]
  const endDate = parseInt(hours) < 6 ? addDays(event.date, 1) : event.date

  const searchParams = createSearchParams({
    text: event.title,
    dates: `${format(event.date, 'yyyyMMdd')}T${formattedStartTime}/${format(
      endDate,
      'yyyyMMdd'
    )}T${formattedEndTime}`,
    details: `${event.description}\n\nhttps://fannaan.events/events/${event.id}`,
    location: `${event.venue} - ${event.address}`,
  })

  return `https://calendar.google.com/calendar/r/eventedit?${searchParams.toString()}`
}

export const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(amount)

export const priceAfterDiscount = (price: number, discountValue: number) =>
  (price * (100 - discountValue)) / 100
